class RestockController extends HTMLElement {
  connectedCallback() {
    if (RestockController.instance) return;
    RestockController.instance = this;

    try {
      const dataScript = this.querySelector('script[type="application/json"]');
      this.config = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('[restock-controller] invalid config', e);
      return;
    }

    this.observed = new WeakSet();
    this.lastVariantId = null;
    this.isRestock = false;

    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const initialId = (idInput && idInput.value) || this.config.initialVariantId;
    this.applyForVariantId(initialId);
    this.bindEvents();
  }

  bindEvents() {
    const sync = () => this.syncFromDom();

    const onVariantUpdate = e => {
      const v = e.detail && e.detail.variant;
      if (v) this.applyForVariantId(v.id);
    };

    document.addEventListener('variant:change', onVariantUpdate);
    document.addEventListener('variant:changed', onVariantUpdate);

    document.addEventListener('change', e => {
      if (e.target && e.target.matches('[data-restock-checkbox]')) {
        this.refreshButtonState();
      }
    });

    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      form.addEventListener('change', sync);
      form.addEventListener('input', sync);
    });

    const isBlockedTarget = e => {
      if (!this.isRestock || this.checkboxesChecked()) return false;
      const path = e.composedPath ? e.composedPath() : [e.target];
      return path.some(el => el && el.matches && (
        el.matches('[data-restock-required="true"]') ||
        el.matches('buy-buttons') ||
        el.matches('.shopify-payment-button')
      ));
    };

    document.addEventListener('pointerdown', e => {
      if (isBlockedTarget(e)) this.flashConsent();
    }, true);

    document.addEventListener('click', e => {
      if (isBlockedTarget(e)) {
        e.preventDefault();
        e.stopPropagation();
        this.flashConsent();
        return;
      }
      // sold-out size with an arriving twin: the buy button navigates to the reserve product instead of submitting
      const reserveBtn = e.target && e.target.closest && e.target.closest('[data-arriving-url]');
      if (reserveBtn && reserveBtn.dataset.arrivingUrl) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = reserveBtn.dataset.arrivingUrl;
      }
    }, true);

    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      form.addEventListener('submit', e => {
        if (this.isRestock && !this.checkboxesChecked()) {
          e.preventDefault();
          this.flashConsent();
          return;
        }
        if (this.arrivingUrl) {
          e.preventDefault();
          window.location.href = this.arrivingUrl;
        }
      });
    });

    window.addEventListener('popstate', sync);
    setInterval(sync, 1500);
  }

  flashConsent() {
    const consent = document.querySelector('[data-restock-consent]:not([hidden])');
    if (!consent) return;
    consent.scrollIntoView({ behavior: 'smooth', block: 'center' });
    consent.classList.remove('restock-consent--pulse');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => consent.classList.add('restock-consent--pulse'));
    });
    const cb = consent.querySelector('[data-restock-checkbox]');
    if (cb) cb.focus({ preventScroll: true });
  }

  syncFromDom() {
    let id = null;

    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (idInput && idInput.value) id = idInput.value;

    if (!id) {
      const urlMatch = window.location.search.match(/[?&]variant=(\d+)/);
      if (urlMatch) id = urlMatch[1];
    }

    if (!id || id === this.lastVariantId) return;
    this.applyForVariantId(id);
  }

  applyForVariantId(id) {
    if (id == null) return;
    const key = String(id);
    const state = this.config.variants[key];
    if (!state) return;
    this.lastVariantId = key;
    this.applyState(state);
  }

  resolveLabel(state) {
    const labels = this.config.labels;
    if (!state) return labels.addToCart;
    if (!state.available) return state.arrivingUrl ? (state.arrivingLabel || labels.reserveSize) : labels.soldOut;
    if (this.config.isPreOrderTpl) return labels.preOrder;
    return labels.addToCart;
  }

  buttons() {
    const sels = new Set(['buy-buttons button']);
    document.querySelectorAll('form[action*="/cart/add"]').forEach(f => {
      if (f.id) sels.add('button[form="' + f.id + '"]');
    });
    const found = document.querySelectorAll([...sels].join(', '));
    return Array.from(found).filter(btn => {
      if (btn.classList.contains('shopify-payment-button__button')) return false;
      const t = btn.type;
      return !t || t === 'submit' || t === 'button';
    });
  }

  consents() {
    return document.querySelectorAll('[data-restock-consent]');
  }

  checkboxes() {
    return document.querySelectorAll('[data-restock-checkbox]');
  }

  paymentButtons() {
    return document.querySelectorAll('.shopify-payment-button');
  }

  setButtonLabel(btn, label) {
    btn.dataset.restockLabel = label;
    if (btn.textContent.trim() !== label) btn.textContent = label;
  }

  watchButton(btn) {
    if (this.observed.has(btn)) return;
    this.observed.add(btn);
    const obs = new MutationObserver(() => {
      const target = btn.dataset.restockLabel;
      if (target && btn.textContent.trim() !== target) {
        btn.textContent = target;
      }
    });
    obs.observe(btn, { childList: true, characterData: true, subtree: true });
  }

  watchHost(host) {
    if (this.observed.has(host)) return;
    this.observed.add(host);
    const obs = new MutationObserver(() => {
      if (this._writing || !this.lastVariantId) return;
      const state = this.config.variants[this.lastVariantId];
      if (state) this.applyState(state);
    });
    obs.observe(host, { childList: true, subtree: true });
  }

  checkboxesChecked() {
    const all = this.checkboxes();
    if (!all.length) return true;
    return Array.from(all).every(cb => cb.checked);
  }

  refreshButtonState() {
    const state = this.lastVariantId && this.config.variants[this.lastVariantId];
    if (!state) return;
    const blockedByConsent = this.isRestock && !this.checkboxesChecked();
    const disable = (!state.available && !state.arrivingUrl) || blockedByConsent;

    this._writing = true;
    this.buttons().forEach(btn => {
      if (this.isRestock) {
        btn.setAttribute('data-restock-required', 'true');
      } else {
        btn.removeAttribute('data-restock-required');
      }
      btn.disabled = disable;
      if (!state.available && state.arrivingUrl) {
        btn.setAttribute('data-arriving-url', state.arrivingUrl);
      } else {
        btn.removeAttribute('data-arriving-url');
      }
    });
    this.paymentButtons().forEach(pb => {
      pb.style.display = blockedByConsent ? 'none' : '';
    });
    Promise.resolve().then(() => { this._writing = false; });
  }

  applyState(state) {
    const label = this.resolveLabel(state);
    this.isRestock = !!(state && state.restock && state.available);
    this.arrivingUrl = (state && !state.available && state.arrivingUrl) || null;

    this._writing = true;
    this.buttons().forEach(btn => {
      this.watchButton(btn);
      this.setButtonLabel(btn, label);
    });
    document.querySelectorAll('buy-buttons').forEach(host => this.watchHost(host));
    Promise.resolve().then(() => { this._writing = false; });

    this.consents().forEach(el => { el.hidden = !this.isRestock; });
    this.checkboxes().forEach(cb => {
      if (this.isRestock) {
        cb.setAttribute('required', '');
      } else {
        cb.removeAttribute('required');
        cb.checked = false;
      }
    });

    document.querySelectorAll('.restock-microcopy').forEach(el => {
      el.hidden = !this.isRestock;
    });

    // sold-out size that the arriving-stock twin carries: the truck line says when it arrives instead of the ship date
    const arrivingSize = !!(state && !state.available && state.arrivingUrl);
    document.querySelectorAll('[data-restock-truckline]').forEach(el => {
      const def = el.querySelector('[data-restock-default]');
      const act = el.querySelector('[data-restock-active]');
      const arr = el.querySelector('[data-restock-arriving]');
      if (def) def.hidden = this.isRestock || (arrivingSize && !!arr);
      if (act) act.hidden = !this.isRestock || (arrivingSize && !!arr);
      if (arr) arr.hidden = !arrivingSize;
    });

    document.querySelectorAll('[data-restock-faster-link]').forEach(el => {
      el.hidden = this.isRestock;
    });

    this.refreshButtonState();
  }
}

if (!customElements.get('restock-controller')) {
  customElements.define('restock-controller', RestockController);
}
